import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import WalletConnect from '../WalletConnect';
import './AdminDashboard.css';

interface Asset {
  id: string;
  name: string;
  type: string;
  tokenSymbol: string;
  contractAddress?: string;
  chainId?: number;
}

const AdminDashboard: React.FC = () => {
  const { state, logout } = useAuth();
  const navigate = useNavigate();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAsset, setNewAsset] = useState({
    name: '',
    type: 'COMPANY',
    jurisdiction: '',
    legalEntityName: '',
    description: '',
    tokenSymbol: '',
    tokenDecimals: '18',
    totalSupply: '',
  });
  const [assigningContract, setAssigningContract] = useState<string | null>(null);
  const [contractAddress, setContractAddress] = useState('');
  const [chainId, setChainId] = useState<number>(11155111); // Default Sepolia

  useEffect(() => {
    loadAssets();
  }, []);

  const loadAssets = async () => {
    setLoading(true);
    setError(null);
    try {
      const assetsData = await apiService.getAssets();
      setAssets(assetsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAsset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const createdAsset = await apiService.createAsset(newAsset);
      setShowCreateForm(false);
      setNewAsset({
        name: '',
        type: 'COMPANY',
        jurisdiction: '',
        legalEntityName: '',
        description: '',
        tokenSymbol: '',
        tokenDecimals: '18',
        totalSupply: '',
      });
      await loadAssets();
      // Optionally navigate to the new asset or start contract assignment
      // navigate(`/assets/${createdAsset.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create asset');
    }
  };

  const handleRegisterContract = async (assetId: string) => {
    if (!contractAddress.trim()) {
      setError('Contract address is required');
      return;
    }
    
    try {
      setError(null);
      await apiService.registerContract(assetId, contractAddress.trim(), chainId);
      setAssigningContract(null);
      setContractAddress('');
      setChainId(11155111);
      await loadAssets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register contract');
    }
  };

  const handleStartAssignContract = (assetId: string) => {
    setAssigningContract(assetId);
    setContractAddress('');
    setChainId(11155111);
    setError(null);
  };

  const handleCancelAssignContract = () => {
    setAssigningContract(null);
    setContractAddress('');
    setChainId(11155111);
    setError(null);
  };

  if (loading) {
    return <div className="admin-dashboard">Loading...</div>;
  }

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <div>
          <h1>Admin Portal</h1>
          <p>Asset Management</p>
        </div>
        <div className="header-actions">
          <WalletConnect />
          <button onClick={() => navigate('/dashboard')} className="nav-button">
            Dashboard
          </button>
          <button onClick={async () => { await logout(); navigate('/', { replace: true }); }} className="logout-button">
            Logout
          </button>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="dashboard-content">
        <section className="assets-section">
          <div className="section-header">
            <h2>Assets</h2>
            <button onClick={() => setShowCreateForm(!showCreateForm)} className="create-button">
              {showCreateForm ? 'Cancel' : 'Create Asset'}
            </button>
          </div>

          {showCreateForm && (
            <form onSubmit={handleCreateAsset} className="create-asset-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={newAsset.name}
                    onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Type *</label>
                  <select
                    value={newAsset.type}
                    onChange={(e) => setNewAsset({ ...newAsset, type: e.target.value })}
                    required
                  >
                    <option value="COMPANY">Company</option>
                    <option value="FUND">Fund</option>
                    <option value="PROPERTY">Property</option>
                    <option value="IP">IP</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Token Symbol *</label>
                  <input
                    type="text"
                    value={newAsset.tokenSymbol}
                    onChange={(e) => setNewAsset({ ...newAsset, tokenSymbol: e.target.value.toUpperCase() })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Total Supply</label>
                  <input
                    type="text"
                    value={newAsset.totalSupply}
                    onChange={(e) => setNewAsset({ ...newAsset, totalSupply: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Legal Entity Name</label>
                <input
                  type="text"
                  value={newAsset.legalEntityName}
                  onChange={(e) => setNewAsset({ ...newAsset, legalEntityName: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newAsset.description}
                  onChange={(e) => setNewAsset({ ...newAsset, description: e.target.value })}
                  rows={3}
                />
              </div>
              <button type="submit" className="submit-button">Create Asset</button>
            </form>
          )}

          {assets.length === 0 ? (
            <p>No assets found. Create your first asset above.</p>
          ) : (
            <div className="assets-table-container">
              <table className="assets-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Symbol</th>
                    <th>Contract</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map((asset) => (
                    <tr key={asset.id}>
                      <td>
                        <a
                          href={`#/assets/${asset.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/assets/${asset.id}`);
                          }}
                          className="asset-link"
                        >
                          {asset.name}
                        </a>
                      </td>
                      <td>{asset.type}</td>
                      <td>{asset.tokenSymbol}</td>
                      <td>
                        {asset.contractAddress ? (
                          <div>
                            <span className="contract-badge">Deployed</span>
                            <div className="contract-info">
                              <small>{asset.contractAddress.slice(0, 10)}...{asset.contractAddress.slice(-8)}</small>
                            </div>
                          </div>
                        ) : assigningContract === asset.id ? (
                          <div className="assign-contract-form">
                            <input
                              type="text"
                              placeholder="0x..."
                              value={contractAddress}
                              onChange={(e) => setContractAddress(e.target.value)}
                              className="contract-input"
                              autoFocus
                            />
                            <input
                              type="number"
                              placeholder="Chain ID"
                              value={chainId}
                              onChange={(e) => setChainId(parseInt(e.target.value) || 11155111)}
                              className="chain-id-input"
                            />
                            <div className="contract-actions">
                              <button
                                onClick={() => handleRegisterContract(asset.id)}
                                className="assign-button"
                              >
                                Assign
                              </button>
                              <button
                                onClick={handleCancelAssignContract}
                                className="cancel-button"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className="no-contract">Not Deployed</span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons-group">
                          {!asset.contractAddress && (
                            <button
                              onClick={() => handleStartAssignContract(asset.id)}
                              className="assign-contract-button"
                              disabled={!!assigningContract}
                            >
                              Assign Contract
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/assets/${asset.id}`)}
                            className="action-button"
                          >
                            Manage
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;

